// src/components/shared/ConfirmDialog.jsx
// Substitui window.confirm() por um modal acessível e estilizado.
//
// USO (imperativo via hook):
//   const { confirm, ConfirmDialogPortal } = useConfirm();
//
//   // No JSX do componente:
//   <ConfirmDialogPortal />
//
//   // Na função:
//   const ok = await confirm({
//     title:   'Remover evento?',
//     message: 'Esta ação não pode ser desfeita.',
//     danger:  true,         // botão vermelho
//     confirmLabel: 'Remover',
//     cancelLabel:  'Cancelar',
//   });
//   if (ok) { /* ... */ }

import React, { useState, useCallback, useRef } from 'react';

// ─────────────────────────────────────────
// COMPONENTE VISUAL
// ─────────────────────────────────────────

function ConfirmDialog({
  title,
  message,
  danger,
  confirmLabel,
  cancelLabel,
  confirmationText,
  confirmationLabel,
  onConfirm,
  onCancel,
}) {
  const [confirmationValue, setConfirmationValue] = useState('');
  const expectedConfirmation = String(confirmationText || '').trim();
  const needsTextConfirmation = expectedConfirmation.length > 0;
  const confirmationMatches = !needsTextConfirmation
    || confirmationValue.trim().toUpperCase() === expectedConfirmation.toUpperCase();

  return (
    <div style={{
      position:        'fixed',
      inset:           0,
      zIndex:          9999,
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'center',
      background:      'rgba(0, 0, 0, 0.65)',
      backdropFilter:  'blur(4px)',
      animation:       'fadeIn 0.15s ease-out',
    }}>
      <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>

      <div style={{
        background:   'var(--bg-base)',
        border:       `1px solid ${danger ? 'rgba(var(--error-rgb),0.4)' : 'rgba(var(--brand-rgb),0.4)'}`,
        borderRadius: '16px',
        padding:      '28px 32px',
        width:        'min(420px, 90vw)',
        boxShadow:    'none',
        animation:    'slideUp 0.2s ease-out',
      }}>
        {/* Ícone + título */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <span style={{ fontSize: '24px' }}>{danger ? '⚠️' : '❓'}</span>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px', fontWeight: 700 }}>
            {title}
          </h3>
        </div>

        {/* Mensagem */}
        {message && (
          <p style={{ margin: '0 0 24px', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6 }}>
            {message}
          </p>
        )}

        {needsTextConfirmation && (
          <div style={{ margin: '0 0 24px' }}>
            <label style={{
              display: 'block',
              color: 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: 600,
              marginBottom: '8px',
            }}>
              {confirmationLabel || `Digite ${expectedConfirmation} para confirmar`}
            </label>
            <input
              autoFocus
              value={confirmationValue}
              onChange={e => setConfirmationValue(e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${confirmationMatches ? 'rgba(var(--brand-rgb),0.35)' : 'rgba(var(--error-rgb),0.35)'}`,
                background: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                fontWeight: 600,
                outline: 'none',
              }}
            />
          </div>
        )}

        {/* Botões */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding:      '10px 20px',
              borderRadius: '8px',
              border:       '1px solid rgba(var(--brand-rgb),0.3)',
              background:   'rgba(var(--brand-rgb),0.1)',
              color:        'var(--text-secondary)',
              cursor:       'pointer',
              fontSize:     '14px',
              fontWeight:   600,
              transition:   'all 0.15s',
            }}
            onMouseEnter={e => e.target.style.background = 'rgba(var(--brand-rgb),0.2)'}
            onMouseLeave={e => e.target.style.background = 'rgba(var(--brand-rgb),0.1)'}
          >
            {cancelLabel || 'Cancelar'}
          </button>

          <button
            autoFocus={!needsTextConfirmation}
            onClick={onConfirm}
            disabled={!confirmationMatches}
            style={{
              padding:      '10px 20px',
              borderRadius: '8px',
              border:       'none',
              background:   danger
                ? 'linear-gradient(135deg, var(--error), #9b1a1a)'
                : 'var(--action)',
              color:        'white',
              cursor:       confirmationMatches ? 'pointer' : 'not-allowed',
              fontSize:     '14px',
              fontWeight:   600,
              transition:   'opacity 0.15s',
              opacity:       confirmationMatches ? 1 : 0.45,
            }}
            onMouseEnter={e => { if (confirmationMatches) e.target.style.opacity = '0.85'; }}
            onMouseLeave={e => { if (confirmationMatches) e.target.style.opacity = '1'; }}
          >
            {confirmLabel || 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// HOOK IMPERATIVO
// ─────────────────────────────────────────

export function useConfirm() {
  const [dialogState, setDialogState] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogState(options);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setDialogState(null);
    resolveRef.current?.(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogState(null);
    resolveRef.current?.(false);
  }, []);

  const ConfirmDialogPortal = useCallback(() => {
    if (!dialogState) return null;
    return (
      <ConfirmDialog
        {...dialogState}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    );
  }, [dialogState, handleConfirm, handleCancel]);

  return { confirm, ConfirmDialogPortal };
}

export default ConfirmDialog;
